import React, { useEffect, useState } from "react";
import { IconButton, Typography } from "@material-ui/core";
import { useIntl } from "react-intl";
import Button from "@material-ui/core/Button";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { CATEGORIES_ALL, TABLE_HEIGHT } from "../../../../utils/constants";
import { useDispatch, useSelector } from "react-redux";
import { changeCategoriesAction, changeSelectedCategoriesAction } from "../../../../reducers/searchOmimState";
import ArrowForwardIosRoundedIcon from "@material-ui/icons/ArrowForwardIosRounded";
import { getCategories } from "../../../../services/commonservices";
import CircularProgress from "@material-ui/core/CircularProgress";

const useStyles = makeStyles((theme) => ({
  superCategoryContainer: {
    display: "flex",
    flexDirection: "column",
    paddingRight: "1rem",
    overflow: "scroll",
    gap: theme.spacing(1),
    height: TABLE_HEIGHT,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  categoryContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(1, 0, 1, 2),
    gap: theme.spacing(1),
  },
  subcategoryContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(1, 0, 1, 2),
  },
  categoryButtonContainer: {
    width: "100%",
  },
  superButtonContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
  },
  superButton: {
    borderRadius: 24,
    cursor: "pointer",
    padding: theme.spacing(1, 2),
    color: theme.palette.text.main + "!important",
    textTransform: "capitalize",
  },
  selectedSuperButton: {
    border: "1px solid #FF3185",
    backgroundColor: "#FFF6FA",
  },
  unselectedSuperButton: {
    border: "1px solid #FFF",
    backgroundColor: "#F3F2FC",
  },
  superButtonObject: {
    width: "100%",
  },
  arrowButton: {
    position: "absolute",
    left: 0,
    zIndex: 1,
  },
  subCategory: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-start",
    textAlign: "left",
    textTransform: "capitalize",
  },
  selectedSubCategory: {
    color: theme.palette.selected.main + "!important",
  },
  unselectedSubCategory: {
    color: theme.palette.text.main + "!important",
  },
  expanded: {
    fontSize: "0.8rem !important",
    transition: "transform 0.5s",
    transform: "rotate(90deg)",
  },
  notExpanded: {
    fontSize: "0.8rem !important",
    transition: "transform 0.5s",
  },
  subCategoryText: {
    fontSize: "0.8rem",
  },
  flexContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 0),
    position: "relative",
  },
  firstCap: {
    textTransform: "lowercase !important",
    "&:first-letter": {
      textTransform: "capitalize !important",
    },
  },
}));

const CategoriesListComponent = () => {
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();

  const selectedCategories = useSelector((state) => state.searchOmim.selectedCategories);
  const categories = useSelector((state) => state.searchOmim.categories);

  const [displayedCategories, setDisplayedCategories] = useState({});
  const [loading, setLoading] = useState(false);

  const getCategoriesCatch = () => {
    setLoading(false);
  };

  const getCategoriesCallback = (response) => {
    dispatch(changeCategoriesAction(response.data));

    const categoriesObject = response.data.reduce((a, v) => ({ ...a, [v.text]: "" }), {});
    setDisplayedCategories(categoriesObject);
    setLoading(false);
  };

  useEffect(() => {
    if (!categories?.length) {
      setLoading(true);
      getCategories(getCategoriesCallback, getCategoriesCatch);
    } else {
      const categoriesObject = categories.reduce((a, v) => ({ ...a, [v.text]: "" }), {});
      setDisplayedCategories(categoriesObject);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectSuperCategory = (superCat) => {
    let parsed = JSON.parse(JSON.stringify(selectedCategories));
    let selected = { ...parsed };

    if (selected[superCat]) {
      delete selected[superCat];
    } else {
      selected[superCat] =
        categories
          .find((c) => c.text === superCat)
          .subcategories.map((cat) => {
            return {
              [cat.text]:
                cat.subcategories
                  ?.map((sub) => {
                    return {
                      [sub.text]:
                        sub.subcategories
                          ?.map((sec) => sec.text)
                          ?.reduce(
                            (a, v) => ({
                              ...a,
                              [v]: "",
                            }),
                            {},
                          ) || "",
                    };
                  })
                  .reduce(
                    (a, v) => ({
                      ...a,
                      ...v,
                    }),
                    {},
                  ) || "",
            };
          })
          .reduce(
            (a, v) => ({
              ...a,
              ...v,
            }),
            {},
          ) || "";
    }

    dispatch(changeSelectedCategoriesAction(selected));
  };

  const handleDisplaySuperCategory = (superCat) => {
    const cats = { ...displayedCategories };
    const subcategories = categories.find((c) => c.text === superCat).subcategories.map((cat) => cat.text);

    cats[superCat] = !!Object.keys(cats[superCat])?.length
      ? ""
      : subcategories.reduce(
          (a, v) => ({
            ...a,
            [v]: "",
          }),
          {},
        );

    setDisplayedCategories(cats);
  };

  const handleDisplayCategory = (superCat, cat) => {
    const cats = { ...displayedCategories };
    const subcategories = categories
      .find((c) => c.text === superCat)
      .subcategories.find((c) => c.text === cat)
      .subcategories.map((c) => c.text);

    cats[superCat][cat] = !!Object.keys(cats[superCat][cat]).length
      ? ""
      : subcategories.reduce(
          (a, v) => ({
            ...a,
            [v]: "",
          }),
          {},
        );

    setDisplayedCategories(cats);
  };

  const handleSelectCategory = (superCat, cat) => {
    let parsed = JSON.parse(JSON.stringify(selectedCategories));
    let selected = { ...parsed };

    if (!selected[superCat]) {
      selected = { ...selected, [superCat]: {} };
    }

    if (!selected[superCat][cat]) {
      selected[superCat][cat] =
        categories
          .find((c) => c.text === superCat)
          .subcategories.find((c) => c.text === cat)
          .subcategories.map((c) => {
            return {
              [c.text]:
                c.subcategories
                  ?.map((sec) => sec.text)
                  ?.reduce(
                    (a, v) => ({
                      ...a,
                      [v]: "",
                    }),
                    {},
                  ) || "",
            };
          })
          .reduce(
            (a, v) => ({
              ...a,
              ...v,
            }),
            {},
          ) || "";
    } else {
      delete selected[superCat][cat];
    }

    if (!Object.keys(selected[superCat])?.length) {
      delete selected[superCat];
    }

    dispatch(changeSelectedCategoriesAction(selected));
  };

  const handleDisplaySubCategory = (superCat, cat, subCat) => {
    const cats = { ...displayedCategories };
    const subcategories = categories
      .find((c) => c.text === superCat)
      .subcategories.find((c) => c.text === cat)
      .subcategories.find((c) => c.text === subCat)
      .subcategories.map((c) => c.text);

    cats[superCat][cat][subCat] = !!Object.keys(cats[superCat][cat][subCat]).length
      ? ""
      : subcategories.reduce(
          (a, v) => ({
            ...a,
            [v]: "",
          }),
          {},
        );

    setDisplayedCategories(cats);
  };

  const handleSelectSubcategory = (superCat, cat, subCat) => {
    let parsed = JSON.parse(JSON.stringify(selectedCategories));
    let selected = { ...parsed };

    if (!selected[superCat] || !selected[superCat][cat]) {
      selected[superCat] = { ...selected[superCat], [cat]: {} };
    }

    if (!selected[superCat][cat][subCat] && selected[superCat][cat][subCat] !== "") {
      if (
        categories
          .find((c) => c.text === superCat)
          .subcategories.find((c) => c.text === cat)
          ?.subcategories.find((c) => c.text === subCat)?.subcategories?.length
      ) {
        selected[superCat][cat][subCat] = categories
          .find((c) => c.text === superCat)
          .subcategories.find((c) => c.text === cat)
          ?.subcategories.find((c) => c.text === subCat)
          ?.subcategories.map((c) => c.text)
          .reduce(
            (a, v) => ({
              ...a,
              [v]: "",
            }),
            {},
          );
      } else {
        selected[superCat][cat][subCat] = "";
      }
    } else {
      delete selected[superCat][cat][subCat];
    }

    if (!Object.keys(selected[superCat][cat])?.length) {
      delete selected[superCat][cat];
    }

    if (!Object.keys(selected[superCat])?.length) {
      delete selected[superCat];
    }

    dispatch(changeSelectedCategoriesAction(selected));
  };

  const handleSelectSecondSubcategory = (superCat, cat, subCat, secondSub) => {
    let parsed = JSON.parse(JSON.stringify(selectedCategories));
    let selected = { ...parsed };

    if (!selected[superCat]) {
      selected = { ...selected, [superCat]: {} };
    }

    if (!selected[superCat][cat]) {
      selected = { ...selected, [superCat]: { ...selected[superCat], [cat]: {} } };
    }

    if (!selected[superCat][cat][subCat]) {
      selected = {
        ...selected,
        [superCat]: { ...selected[superCat], [cat]: { ...selected[superCat][cat], [subCat]: {} } },
      };
    }

    if (selected[superCat][cat][subCat][secondSub] === "") {
      delete selected[superCat][cat][subCat][secondSub];
    } else {
      selected[superCat][cat][subCat][secondSub] = "";
    }

    if (!Object.keys(selected[superCat][cat][subCat])?.length) {
      delete selected[superCat][cat][subCat];
    }

    if (!Object.keys(selected[superCat][cat])?.length) {
      delete selected[superCat][cat];
    }

    if (!Object.keys(selected[superCat])?.length) {
      delete selected[superCat];
    }

    dispatch(changeSelectedCategoriesAction(selected));
  };

  const isSuperCategorySelected = (superCat) => {
    if (!Object.keys(selectedCategories)?.length) {
      return false;
    }

    const superCategorySelected = !!selectedCategories[superCat];
    const noSubCategories =
      categories[superCat]?.length === 1 && categories[superCat][0].category_name === CATEGORIES_ALL;
    let allCategoriesSelected = false;

    if (selectedCategories[superCat]) {
      allCategoriesSelected =
        Object.keys(selectedCategories[superCat])?.length ===
        categories.find((c) => c.text === superCat)?.subcategories.length;
    }

    return (!!superCategorySelected && !!allCategoriesSelected) || (!!superCategorySelected && !!noSubCategories);
  };

  const isCategorySelected = (superCat, cat) => {
    if (!selectedCategories[superCat] || !Object.keys(selectedCategories[superCat]).includes(cat)) {
      return false;
    }

    const categorySelected = selectedCategories[superCat][cat] === "";
    const allSubCategoriesSelected =
      Object.keys(selectedCategories[superCat][cat])?.length ===
      categories.find((c) => c.text === superCat).subcategories.find((c) => c.text === cat).subcategories?.length;

    return !!categorySelected || !!allSubCategoriesSelected;
  };

  const isSubcategorySelected = (superCat, cat, subCat) => {
    if (
      !selectedCategories[superCat] ||
      !selectedCategories[superCat][cat] ||
      (selectedCategories[superCat][cat] && !Object.keys(selectedCategories[superCat][cat]).includes(subCat))
    ) {
      return false;
    }

    const subCategorySelected = selectedCategories[superCat][cat][subCat] === "";
    const allSecondSubCategoriesSelected =
      Object.keys(selectedCategories[superCat][cat][subCat])?.length ===
      categories
        .find((c) => c.text === superCat)
        .subcategories.find((c) => c.text === cat)
        .subcategories.find((c) => c.text === subCat).subcategories?.length;

    return !!subCategorySelected || !!allSecondSubCategoriesSelected;
  };

  const isSecondSubcategorySelected = (superCat, cat, subCat, secondSub) => {
    return (
      selectedCategories[superCat] &&
      selectedCategories[superCat][cat] &&
      selectedCategories[superCat][cat][subCat] &&
      selectedCategories[superCat][cat][subCat][secondSub] === ""
    );
  };

  const isSuperCatExpanded = (superCat) => {
    return !!Object.keys(displayedCategories[superCat])?.length;
  };

  const isCatExpanded = (superCat, cat) => {
    return !!Object.keys(displayedCategories[superCat][cat])?.length;
  };

  const isSubCatExpanded = (superCat, cat, subCat) => {
    return !!Object.keys(displayedCategories[superCat][cat][subCat])?.length;
  };

  const hasCategories = (superCat) => {
    const children = categories?.find((c) => c.text === superCat)?.subcategories?.length > 1;
    return !!children;
  };

  const hasSubcategories = (superCat, cat) => {
    return !!categories.find((c) => c.text === superCat)?.subcategories.find((c) => c.text === cat).subcategories
      ?.length;
  };

  const hasSecondSubcategories = (superCat, cat, subCat) => {
    return !!categories
      .find((c) => c.text === superCat)
      ?.subcategories.find((c) => c.text === cat)
      .subcategories.find((c) => c.text === subCat)?.subcategories?.length;
  };

  return (
    <>
      <Typography variant={"h2"}>{intl.formatMessage({ id: "category" })}</Typography>
      <div className={classes.superCategoryContainer}>
        {loading && (
          <div className={classes.loadingContainer}>
            <CircularProgress color={"secondary"} />
          </div>
        )}

        {Object.keys(displayedCategories)?.map((superCategory) => (
          <div key={superCategory}>
            <div
              className={`${classes.flexContainer} ${classes.superButton} ${
                isSuperCategorySelected(superCategory) ? classes.selectedSuperButton : classes.unselectedSuperButton
              }`}
            >
              {!!hasCategories(superCategory) && (
                <IconButton
                  onClick={() => handleDisplaySuperCategory(superCategory)}
                  disableRipple
                  className={classes.arrowButton}
                >
                  <ArrowForwardIosRoundedIcon
                    className={!!isSuperCatExpanded(superCategory) ? classes.expanded : classes.notExpanded}
                  />
                </IconButton>
              )}
              <div className={classes.superButtonContainer}>
                <Button
                  className={classes.superButtonObject}
                  disableRipple
                  onClick={() => handleSelectSuperCategory(superCategory)}
                  key={superCategory}
                  variant={"outlined"}
                >
                  <Typography variant={"body1"} className={classes.firstCap}>
                    {superCategory}
                  </Typography>
                </Button>
              </div>
            </div>

            {!!Object.keys(displayedCategories[superCategory])?.length && (
              <div className={classes.categoryContainer}>
                {Object.keys(displayedCategories[superCategory])?.map((category) => (
                  <div key={category} className={classes.categoryButtonContainer}>
                    <div
                      className={`${classes.flexContainer} ${classes.superButton} ${
                        isCategorySelected(superCategory, category)
                          ? classes.selectedSuperButton
                          : classes.unselectedSuperButton
                      }`}
                    >
                      {!!hasSubcategories(superCategory, category) && (
                        <IconButton
                          onClick={() => handleDisplayCategory(superCategory, category)}
                          disableRipple
                          className={classes.arrowButton}
                        >
                          <ArrowForwardIosRoundedIcon
                            className={
                              !!isCatExpanded(superCategory, category) ? classes.expanded : classes.notExpanded
                            }
                          />
                        </IconButton>
                      )}
                      <div className={classes.superButtonContainer}>
                        <Button
                          className={classes.superButtonObject}
                          disableRipple
                          onClick={() => handleSelectCategory(superCategory, category)}
                          key={superCategory}
                          variant={"outlined"}
                        >
                          <Typography variant={"body1"} className={classes.firstCap}>
                            {category}
                          </Typography>
                        </Button>
                      </div>
                    </div>

                    {!!Object.keys(displayedCategories[superCategory][category])?.length && (
                      <div className={classes.subcategoryContainer}>
                        {Object.keys(displayedCategories[superCategory][category]).map((subcategory) => (
                          <div key={subcategory} className={classes.categoryButtonContainer}>
                            <div
                              className={`${classes.flexContainer} ${classes.superButton} ${
                                isSubcategorySelected(superCategory, category, subcategory)
                                  ? classes.selectedSuperButton
                                  : classes.unselectedSuperButton
                              }`}
                            >
                              {!!hasSecondSubcategories(superCategory, category, subcategory) && (
                                <IconButton
                                  onClick={() => handleDisplaySubCategory(superCategory, category, subcategory)}
                                  disableRipple
                                  className={classes.arrowButton}
                                >
                                  <ArrowForwardIosRoundedIcon
                                    className={
                                      !!isSubCatExpanded(superCategory, category, subcategory)
                                        ? classes.expanded
                                        : classes.notExpanded
                                    }
                                  />
                                </IconButton>
                              )}
                              <div className={classes.superButtonContainer}>
                                <Button
                                  className={classes.superButtonObject}
                                  disableRipple
                                  onClick={() => handleSelectSubcategory(superCategory, category, subcategory)}
                                  key={superCategory}
                                  variant={"outlined"}
                                >
                                  <Typography variant={"body1"} className={classes.firstCap}>
                                    {subcategory}
                                  </Typography>
                                </Button>
                              </div>
                            </div>

                            {!!Object.keys(displayedCategories[superCategory][category][subcategory])?.length && (
                              <div className={classes.subcategoryContainer}>
                                {Object.keys(displayedCategories[superCategory][category][subcategory]).map(
                                  (secondSub) => (
                                    <Button
                                      key={secondSub}
                                      onClick={() =>
                                        handleSelectSecondSubcategory(superCategory, category, subcategory, secondSub)
                                      }
                                      className={`${classes.subCategory} ${
                                        !!isSecondSubcategorySelected(superCategory, category, subcategory, secondSub)
                                          ? classes.selectedSubCategory
                                          : classes.unselectedSubCategory
                                      }`}
                                    >
                                      <Typography variant={"body1"} className={classes.subCategoryText}>
                                        {secondSub}
                                      </Typography>
                                    </Button>
                                  ),
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default CategoriesListComponent;
